// frontend/src/pages/storeActivity/primitives.jsx
//
// The small parts every Store Activity section is built from. Nothing here
// knows what a store is — a panel frame, a label-over-value cell, a stat
// tile, the day-by-day bar chart, the source chip and a relative time.

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@mui/material';
import { useFormatters } from '../../hooks/useFormatters';
import { relativeTime } from '../../utils/relativeTime';
import { EMPTY } from './text';

export function Panel({ icon, title, subtitle, count, actions, className = '', children }) {
  return (
    <section className={`sa-panel ${className}`}>
      <div className="sa-panel-head">
        {icon}
        <span className="sa-panel-title">{title}</span>
        {count != null && <span className="sa-panel-count">{count}</span>}
        {actions && <span className="sa-panel-actions">{actions}</span>}
      </div>
      {subtitle && <div className="sa-panel-subtitle">{subtitle}</div>}
      <div className="sa-panel-body">{children}</div>
    </section>
  );
}

export function DefGroup({ title, children }) {
  return (
    <div className="sa-def-group">
      {title && <div className="sa-def-head">{title}</div>}
      <div className="sa-defs">{children}</div>
    </div>
  );
}

export function Def({ label, value, warn, wide }) {
  const missing = value === null || value === undefined || value === '';
  return (
    <div className={`sa-def${wide ? ' sa-def--wide' : ''}`}>
      <span className="sa-def-label">{label}</span>
      <span className={`sa-def-value${warn ? ' sa-def-value--warn' : ''}`}>
        {missing ? EMPTY : value}
      </span>
    </div>
  );
}

/**
 * A headline number with its label under it, and an optional second line.
 * `warn` colours the value — used for "Access: denied" and nothing else.
 */
export function Stat({ label, value, note, warn }) {
  return (
    <div className="sa-stat">
      <div className={`sa-stat-value${warn ? ' sa-stat-value--warn' : ''}`}>{value}</div>
      <div className="sa-stat-label">{label}</div>
      {note && <div className="sa-stat-note">{note}</div>}
    </div>
  );
}

/**
 * One column per day, each a stack of `series` segments scaled to the
 * busiest day, then a legend.
 *
 * `series` is in stacking order, bottom first: `{ key, label, modifier }`,
 * where `key` reads the day's count and `modifier` picks the
 * `sa-eng-seg--*` / `sa-swatch--*` colour. The counts must not overlap —
 * the segments are added, so a count that already includes another would be
 * drawn twice. The legend and the tooltip list the series top first, the
 * order the eye reads a column in.
 *
 * Zero days stay as empty slots: the gaps are what these charts are read for.
 */
export function DailyBars({ days, series, label }) {
  const { formatNumber, formatDate } = useFormatters();
  const total = (day) => series.reduce((sum, s) => sum + day[s.key], 0);
  const peak = Math.max(...days.map(total));
  const topFirst = [...series].reverse();
  return (
    <>
      <div className="sa-eng" role="img" aria-label={label}>
        {days.map((d, i) => {
          // Percent of the plot height, so the tallest day fills it and the
          // rest scale from the same baseline.
          const height = (n) => `${(n / peak) * 100}%`;
          const tooltip = [
            formatDate(d.date),
            ...topFirst.map((s) => `${s.label}: ${formatNumber(d[s.key], 0)}`),
          ].join(' · ');
          // Day-of-month under every seventh slot, counted back from today so
          // the last slot is always labelled; the tooltip carries the full
          // date. Read off the ISO string rather than a Date, which would
          // shift it by the viewer's zone.
          const tick = (days.length - 1 - i) % 7 === 0 ? Number(d.date.slice(8, 10)) : null;
          return (
            <Tooltip key={d.date} title={tooltip}>
              <div className={`sa-eng-day${total(d) ? '' : ' sa-eng-day--quiet'}`}>
                <div className="sa-eng-col">
                  {series.map((s) => (d[s.key] > 0
                    ? <span key={s.key} className={`sa-eng-seg sa-eng-seg--${s.modifier}`} style={{ height: height(d[s.key]) }} />
                    : null))}
                </div>
                <span className="sa-eng-tick">{tick ?? ''}</span>
              </div>
            </Tooltip>
          );
        })}
      </div>
      <div className="sa-legend">
        {topFirst.map((s) => (
          <span className="sa-legend-item" key={s.key}>
            <span className={`sa-swatch sa-swatch--${s.modifier}`} />{s.label}
          </span>
        ))}
      </div>
    </>
  );
}

export function SourceChip({ source }) {
  const { t } = useTranslation();
  return (
    <span className={`sa-chip sa-chip--${source}`}>
      {/* No defaultValue: MerchantEvent.SOURCE_CHOICES is a closed vocabulary
          and every member is pinned to a string by
          core/test_i18n_dynamic_key_contracts.py. A fallback here would render
          the raw code as if it were copy, hiding the missing translation. */}
      {t(`storeActivity.source.${source}`)}
    </span>
  );
}

/** How long ago `value` was, with the full date and time on hover. */
export function RelativeTime({ value, className }) {
  const { t } = useTranslation();
  const { formatDateTime } = useFormatters();
  return (
    <Tooltip title={formatDateTime(value)} describeChild>
      <span className={className}>{relativeTime(value, t)}</span>
    </Tooltip>
  );
}
